import { createAsyncThunk } from "@reduxjs/toolkit";
import { Axios } from "api/axios";
import axios from "axios";
import { API_ENDPOINT } from "helpers/Endpoints/endpoints";
import { FilterBuilder } from "helpers/ThunkHelper/FilterBuilder";
const API_URL = process.env.REACT_APP_BASE_URL;
const headers = {
  "Content-Type": "application/json",
};

export interface Warehouse {
  id?: number;
  packageName: string;
  districtCode: string;
  districtName: string;
  state: string;
  status: boolean;
  createBy?: string;
  updatedBy?: string;
  createDate?: string;
  updateDate?: string;
}
export interface PaginatedResponse<T> {
  count: number;
  total_pages: number;
  next: string | null;
  previous: string | null;
  results: T[];
}
interface ColumnFilterResponse {
  data: string[];
}
interface ColumnDropdownPayload {
  column: string;
  data: string[];
}

// Main fetchWarehouse thunk
export const fetchWarehouse = createAsyncThunk(
  "warehouse/Warehouse",
  async ({
    page,
    pageSize,
    sort,
    order,
    search,
    filters,
  }: {
    page: number;
    pageSize: number;
    search?: string;
    sort?: string;
    order?: "asc" | "desc" | "";
    filters?: any;
  }) => {
    try {
      const params = new URLSearchParams();
      params.append("page", page.toString());
      params.append("page_size", pageSize.toString());

      if (search) params.append("search", search?.trim() || "");
      if (sort) {
        const orderingValue = order === "desc" ? `-${sort}` : sort;
        params.append("ordering", orderingValue);
      }
      const filterPayload = FilterBuilder(filters ?? []);

      const body = {
        filters: filterPayload,
      };
      const response: any = await Axios.post(
        `${API_ENDPOINT?.MASTERS?.WAREHOUSE?.LIST}?${params.toString()}`,
        body,
      );
      return {
        data: response.data.data || [],
        rowCount: response.data.count || 0,
      };
    } catch (error: any) {
      throw new Error("Failed to fetch warehouses");
    }
  },
);

export const fetchWarehouseById = createAsyncThunk(
  "warehouse/fetchWarehouseById",
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await Axios.get(`${API_URL}/organisation/${id}/`);

      return response;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data || "Failed to fetch organisations by ID",
      );
    }
  },
);

export const updateWarehouse = createAsyncThunk(
  "warehouse/updateWarehouse",
  async (packageData: any) => {
    const response: any = await Axios.put(
      `${API_URL}api/warehouse/${packageData.id}/update/`,
      packageData,
    );
    //console.log('updateOrganisation response:', response)
    return response;
  },
);

export const updateStatusInWarehouse = createAsyncThunk(
  "warehouse/updateStatusInWarehouse",
  async ({
    id,
    warehouse_status,
  }: {
    id: string;
    warehouse_status: boolean;
  }) => {
    try {
      const response = await Axios.patch(
        `${API_ENDPOINT?.MASTERS?.WAREHOUSE?.STATUS_UPDATE(id)}`,
        {
          warehouse_id: id,
          warehouse_status,
        },
      );
      return response.data;
    } catch (error: any) {
      console.error(
        "Status update failed:",
        error.response?.data || error.message,
      );
      throw error;
    }
  },
);

export const updateStatusInWarehouseFiFo = createAsyncThunk(
  "warehouse/updateStatusInWarehouseFiFo",
  async ({ id, enable_FIFO }: { id: string; enable_FIFO: boolean }) => {
    try {
      const response = await Axios.put(
        `${API_ENDPOINT?.MASTERS?.WAREHOUSE?.ENABLE_FIFO_STATUS(id)}`,
        {
          warehouse_id: id,
          enable_FIFO: enable_FIFO,
        },
      );
      return response.data;
    } catch (error: any) {
      console.error(
        "Status update failed:",
        error.response?.data || error.message,
      );
      throw error;
    }
  },
);

export const addWarehouse = createAsyncThunk(
  "warehouse/addWarehouse",
  async (packageData: any) => {
    const response = await Axios.post(
      `${API_URL}/warehouse/Warehouse_create/`,
      packageData,
    );
    //console.log('addWarehose response:', response)
    //console.log('addWaehosue response:', response.data)
    return response.data;
  },
);

export const deleteWarehouse = createAsyncThunk(
  "warehouse/delete",
  async (id: number, { rejectWithValue }) => {
    try {
      await axios.delete(`${API_URL}/${id}`, { headers });
      return id;
    } catch (err: any) {
      console.error("Delete error:", err);
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  },
);

export const fetchWarehouseColumnDropdown = createAsyncThunk<
  ColumnDropdownPayload,
  string
>(
  "warehouse/fetchWarehouseColumnDropdown",
  async (column, { rejectWithValue }) => {
    try {
      const response = await Axios.get(
        `${API_URL}/warehouse/warehouse/column_filter/`,
        {
          params: { column_filter: column },
          headers,
        },
      );

      const rawData = response.data?.data || [];

      const uniqueValuesSet = new Set(
        rawData
          .map((item: any) => item?.[column])
          .filter((value: string | null) => value !== null && value !== ""),
      );

      const data = Array.from(uniqueValuesSet) as string[];

      return { column, data };
    } catch (err: any) {
      console.error("Error fetching dropdown:", err);
      return rejectWithValue(err.response?.data || "Failed to fetch dropdown");
    }
  },
);

// Exports warehouse CSV file
export const exportWarehouseCSV = createAsyncThunk(
  "warehosue/exportWarehouseCSV",
  async (_, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("authToken");
      const headers = {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
        Accept: "*/*",
      };
      const response = await Axios.get("/warehouse/list/?export=excel", {
        headers,
        responseType: "blob",
      });
      return {
        blob: response.data,
        filename:
          response.headers["content-disposition"]
            ?.split("filename=")[1]
            ?.replace(/"/g, "") || "warehouse.csv",
      };
    } catch (err: any) {
      console.error("Export CSV error:", err);
      return rejectWithValue(err.message || "Failed to export CSV");
    }
  },
);

export const WarehosuesxportsCSV = async ({
  search,
  filters,
}: {
  search?: string;
  filters?: any;
}) => {
  const params = new URLSearchParams();

  if (search) params.append("search", search);

  const filterPayload = FilterBuilder(filters ?? []);

  const response = await Axios.post(
    `${API_ENDPOINT?.MASTERS?.WAREHOUSE?.EXPORT}?${params.toString()}`,
    filterPayload,
    {
      responseType: "blob",
    },
  );

  return {
    blob: response.data,
    filename:
      response.headers["content-disposition"]
        ?.split("filename=")[1]
        ?.replace(/"/g, "") || "Warehouse.csv",
  };
};

export const fetchAllDropdownsWarehouse = createAsyncThunk(
  "warehouse/fetchAllDropdownsWarehouse",
  async (column: string, { rejectWithValue }) => {
    try {
      const response = await Axios.get(
        `/api/warehouse/warehouse/column_filter/`,
        { params: { colum_filter: column } }, // corrected spelling: "colum_filter"
      );

      const dropdownData = response.data?.data || response.data || [];

      return { column, data: dropdownData };
    } catch (err: any) {
      return rejectWithValue(err.response?.data || "Failed to fetch dropdown");
    }
  },
);

// new thunks

export const fetchWarehouseDropDown = createAsyncThunk(
  "model/fetchByDropDown",
  async (_, { rejectWithValue }) => {
    try {
      const response = await Axios.get(
        `${API_ENDPOINT?.MASTERS?.WAREHOUSE?.DROPDOWN}`,
      );
      return response.data;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data || "Failed to fetch Model list",
      );
    }
  },
);

export const getWarehouseByUser = createAsyncThunk(
  "warehouse/getWarehouseByUser",
  async (_, { rejectWithValue }) => {
    try {
      const response = await Axios.get(
        `${API_ENDPOINT?.MASTERS?.WAREHOUSE?.CONFIGURATION?.WAREHOUSE_BY_USER}`,
      );
      console.log("WAREHOUSE BY USER", response.data);
      return response.data;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data || "Failed to fetch Model list",
      );
    }
  },
);

export const fetchItemInwardAnalytics = createAsyncThunk(
  "warehouse/fetchItemInwardAnalytics",
  async (warehouseId: string | undefined, { rejectWithValue }) => {
    try {
      const params = warehouseId
        ? { params: { warehouse_id: warehouseId } }
        : {};
      const response = await Axios.get(
        `${API_ENDPOINT?.WAREHOUSE?.ITEM_INWARD?.ANALYTICS}`,
        params,
      );
      return response.data;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data || "Failed to fetch item inward analytics",
      );
    }
  },
);
